import qs from 'qs'
import { useEffect, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import InfiniteLoader from 'react-window-infinite-loader'
import { Msg, useMsg } from '~/common/localizationUtils'
import { Box, Flex, Skeleton, EmptyState, NoResults, VisuallyHidden } from '~/design_system'
import fetch from '~/common/fetch'
import jsonata from '~/common/jsonata'
import { Search } from '~/components/v2/Search'
import { PageErrorState, SadStates } from '../../SadStates'
import { CheckboxList } from './index'

const fetchItems = async ({ queryKey: [_, params, transform, searchParamKey], pageParam }) => {
  const { url, offset, limit, searchTerm } = params
  const [path, queries] = url.split('?')
  const normalizedUrl =
    pageParam ||
    `${path}?${qs.stringify({
      ...qs.parse(queries),
      limit,
      offset,
      ...(searchTerm?.length > 0 && { [searchParamKey]: searchTerm })
    })}`
  const response = await fetch.get(normalizedUrl)
  const results = transform
    ? jsonata(transform).evaluate({ results: response?.results ?? response }) || []
    : response.results || response
  return {
    itemCount: response.count || response.length,
    results,
    next_link: response.next_link
  }
}

const AsyncCheckboxList = ({ id, optionsFromApi, onChange, value, config }) => {
  const { url, transform, pageSize = 100, isSearchable = true, searchParamKey = 'search' } = optionsFromApi

  const [searchTerm, setSearchTerm] = useState('')
  const [itemCount, setItemCount] = useState(0)
  const [fetchedData, setFetchedData] = useState([])

  const { data, fetchNextPage, isFetchingNextPage, isFetching, isLoading, isError, isSuccess, hasNextPage } =
    useInfiniteQuery({
      queryKey: [`filter-${id}`, { url, limit: pageSize, offset: 0, searchTerm }, transform, searchParamKey],
      queryFn: fetchItems,
      getNextPageParam: lastPage => lastPage.next_link
    })

  const isItemLoaded = index => index < fetchedData.length
  const dataLength = data ? data.pages.flat().length : 0
  // The query key is used to determine if new data has been fetched and if the state should be updated
  const queryKey = searchTerm?.length > 0 ? `${searchTerm}-${dataLength}` : dataLength

  useEffect(() => {
    if (!data) return
    // We need to set the item count so that react-lazy-loader knows totally how many items are there
    /*
      eslint-disable-next-line react-hooks/set-state-in-effect --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: Deriving itemCount/fetchedData from query data causes extra renders.
      Possible fix approach: Compute inline instead of state. memoization can be handled manually if needed:
        // Remove: const [itemCount, setItemCount] = useState(0)
        // Remove: const [fetchedData, setFetchedData] = useState([])
        // Remove: this useEffect
        const itemCount = data?.pages?.[0]?.itemCount || 0
        const fetchedData = data?.pages?.flatMap(group => group.results) || []
    */
    setItemCount(data.pages?.length > 0 ? data.pages[0]?.itemCount : 0)
    const result = data ? data.pages.map(group => group.results).flat() : []
    setFetchedData(result)
  }, [queryKey])

  const msg = useMsg()

  return (
    <>
      {isSearchable && (
        <Box mb={2} data-testid="async-cb-list-search-container">
          <Search value={searchTerm} onChange={setSearchTerm} size="sm" data-testid="async-cb-list-search" />
          <VisuallyHidden aria-live="assertive" role="region" aria-atomic="true">
            <Msg id="common.ui.filtered_results" count={fetchedData.length || 0} />
          </VisuallyHidden>
        </Box>
      )}
      <SadStates
        states={[
          {
            when: isLoading,
            render: () => (
              <>
                <Skeleton w="full" height={4} my={4} />
                <Skeleton w="full" height={4} my={4} />
              </>
            )
          },
          {
            when: isError,
            render: () => (
              <PageErrorState message="There was an error processing your request. Please try again later." />
            )
          },
          {
            when: isSuccess && !fetchedData.length && !hasNextPage,
            render: () => (
              <EmptyState p={0}>
                <NoResults title={msg('sidebar_filters.filter.no_results')} />
              </EmptyState>
            )
          }
        ]}>
        {() => (
          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={async () => {
              if (hasNextPage) await fetchNextPage()
            }}>
            {({ onItemsRendered, ref }) => (
              <>
                <CheckboxList
                  options={fetchedData}
                  onChange={onChange}
                  value={value}
                  config={config}
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                />
                {(isLoading || isFetchingNextPage || isFetching) && (
                  <Box textAlign="center">
                    <Flex>
                      <Skeleton w="full" height={4} my={2} flex="1" mr={2} />
                      <Skeleton w="full" height={4} my={2} flex="12" />
                    </Flex>
                    <Flex>
                      <Skeleton w="full" height={4} my={2} flex="1" mr={2} />
                      <Skeleton w="full" height={4} my={2} flex="12" />
                    </Flex>
                  </Box>
                )}
              </>
            )}
          </InfiniteLoader>
        )}
      </SadStates>
    </>
  )
}
export default AsyncCheckboxList
